import type { FC, ReactNode } from 'react';

import { Box, Stack, Text } from '@eneco/sparky';
import type { BoxProps } from '@eneco/sparky/types';

import {
  circleBoxRecipe,
  promoBoxContainer,
  promoBoxWrapperRecipe,
  titleText,
  titleTextOffset,
  subTitleText,
} from './PromoBox.css';

const COLOR_TEXT_MAP = {
  backgroundCMSVarTwelve: 'textInverted', // Purple, white text
  backgroundCMSVarTen: 'textInverted', // Dark green, white text
  backgroundCMSVarEleven: 'textPrimary', // Light gray, black text
  backgroundCMSVarNine: 'textBrand', // Light green, dark green text
} as const;

type TextColor = (typeof COLOR_TEXT_MAP)[keyof typeof COLOR_TEXT_MAP];

const getTextColor = (backgroundColor?: string): TextColor => {
  if (!backgroundColor) return 'textBrand';
  return COLOR_TEXT_MAP[backgroundColor as keyof typeof COLOR_TEXT_MAP] || 'textPrimary';
};

export interface PromoBoxProps {
  /** Already-rendered rich text; the title is HTML on both providers. */
  title?: ReactNode;
  topSubTitle?: string;
  bottomSubTitle?: string;
  backgroundColorOption?: string;
  isElevated?: boolean;
  desktopPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  mobilePosition?: 'top-left' | 'top-right';
}

/**
 * The circular promo badge, with its own positioning and responsive type scale — the reason this lives in
 * `libs/custom-components` rather than the CMS-agnostic lane directly (`dxp-rules/no-custom-styling`).
 * Markup and stylesheet are the legacy `libs/sitecore/components/content/src/PromoBox/PromoBox.tsx`'s,
 * minus its CMS reads, which the lane's `PromoBox` bridge supplies as props.
 */
export const PromoBox: FC<PromoBoxProps> = ({
  title,
  topSubTitle,
  bottomSubTitle,
  backgroundColorOption,
  isElevated = false,
  desktopPosition,
  mobilePosition,
}) => {
  const backgroundColor = (backgroundColorOption || 'backgroundCMSVarNine') as BoxProps['backgroundColor'];
  const textColor = getTextColor(backgroundColorOption);
  const hasBothSubtitles = Boolean(topSubTitle && bottomSubTitle);

  return (
    <Box className={promoBoxContainer}>
      <Box className={promoBoxWrapperRecipe({ desktopPosition, mobilePosition })}>
        <Box backgroundColor={backgroundColor} borderRadius="round" className={circleBoxRecipe({ elevated: isElevated })}>
          <Stack gap="1" alignX="center">
            {topSubTitle && (
              <Stack.Item>
                <Text color={textColor} className={subTitleText}>
                  {topSubTitle}
                </Text>
              </Stack.Item>
            )}
            {title && (
              <Stack.Item>
                <Text color={textColor} className={hasBothSubtitles ? `${titleText} ${titleTextOffset}` : titleText}>
                  {title}
                </Text>
              </Stack.Item>
            )}
            {bottomSubTitle && (
              <Stack.Item>
                <Text color={textColor} className={subTitleText}>
                  {bottomSubTitle}
                </Text>
              </Stack.Item>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
